import PropTypes from 'prop-types';
import React from 'react';
import { ProgressBar, Spinner } from 'react-bootstrap';
import ConvertedUnits from 'src/components/ConvertedUnits';
import { formatThreeDecimalsNumber } from 'src/utils/conversions';

const RemainingQuantityProgressBar = ({
                                        initialQty,
                                        remainingQty,
                                        reconciledQtyPercentage,
                                        baseUnits = '',
                                        actionWeight,
                                        isActionWeightLoading = false,
                                        showActionDetails,
                                      }) => {
  // Show a spinner while the action weight is loading.
  if (isActionWeightLoading) {
    return <Spinner animation="border" size="sm" />;
  }

  // Determine if the action bar should be displayed.
  const shouldShowAction =
    showActionDetails && remainingQty > 0 && actionWeight >= 0;

  // Determine the type of action based on actionWeight compared to remainingQty.
  const getActionType = (weight, remaining) => {
    if (weight > remaining) return 'over';
    if (weight === remaining) return 'exact';
    return 'under';
  };

  const actionType = shouldShowAction ? getActionType(actionWeight, remainingQty) : null;

  // Calculate the action percentage relative to the initial quantity.
  const rawActionPercent = shouldShowAction ? Math.round((actionWeight / initialQty) * 100) : 0;
  const actionPercentOfTotal = Math.min(rawActionPercent, 100 - reconciledQtyPercentage);

  // Compute the widths of the reconciled and action segments.
  const getBarWidths = () => {
    if (!shouldShowAction) {
      return { reconciled: reconciledQtyPercentage, action: 0 };
    }

    let computedActionWidth = actionPercentOfTotal;
    // Ensure small action amounts are still visible.
    if (computedActionWidth > 0 && computedActionWidth < 1) {
      computedActionWidth = 1;
    }
    // Cap the action width so that the total doesn't exceed 100%.
    computedActionWidth = Math.min(computedActionWidth, 100 - reconciledQtyPercentage);

    return { reconciled: reconciledQtyPercentage, action: computedActionWidth };
  };

  const { reconciled: reconciledWidth, action: actionWidth } = getBarWidths();

  // A helper to display a label when the percentage is significant enough.
  const getLabel = (percentage) => (percentage >= 5 ? `${percentage}%` : '');

  const getReconciledLabel = () => getLabel(reconciledWidth);
  const getActionLabel = () => (shouldShowAction ? getLabel(actionPercentOfTotal) : '');

  // Helper to get the background color for the Action Bar.
  const getBackgroundColor = (type) => {
    if (type === 'under') return '#4CAF50';
    if (type === 'over') return '#dc3545';
    return undefined;
  };

  // Calculate the remaining quantity after the action is taken.
  const displayRemainingQty = shouldShowAction ? remainingQty - actionWeight : remainingQty;

  return (
    <div>
      <ProgressBar className="transaction-progress-bar mt25">
        {/* Reconciled Bar - what has already been used */}
        {reconciledWidth > 0 && (
          <ProgressBar
            key="reconciled"
            variant="warning"
            now={reconciledWidth}
            label={getReconciledLabel()}
            style={{
              background: '#FFC107',
              textAlign: 'left',
              paddingLeft: '10px',
            }}
          />
        )}

        {/* Action Bar - what will be used in this transaction */}
        {shouldShowAction && actionWidth > 0 && (
          <ProgressBar
            key="action"
            variant={actionType === 'over' ? 'danger' : 'success'}
            now={actionWidth}
            label={getActionLabel()}
            style={{
              background: getBackgroundColor(actionType),
              textAlign: 'left',
              paddingLeft: '10px',
            }}
          />
        )}

        {/* Remaining Space */}
        {(reconciledWidth + actionWidth) < 100 && (
          <ProgressBar
            key="remaining"
            variant="light"
            now={100 - (reconciledWidth + actionWidth)}
            style={{ background: '#E9ECEF' }}
          />
        )}
      </ProgressBar>

      {/* Quantity Information */}
      <div className="d-flex align-items-center justify-content-between mt-1">
        <div className="d-flex align-items-center">
          <div>Initial Qty:</div>
          <strong className="d-flex align-items-center">
            &nbsp;
            <ConvertedUnits quantity={initialQty} units={baseUnits} />
          </strong>
        </div>

        <div className="d-flex align-items-center">
          <div>Remaining Qty:</div>
          <strong className="d-flex align-items-center">
            &nbsp;
            <span style={{ color: displayRemainingQty < 0 ? 'red' : 'inherit' }}>
              <ConvertedUnits quantity={formatThreeDecimalsNumber(displayRemainingQty)} units={baseUnits} />
            </span>
          </strong>
        </div>
      </div>
    </div>
  );
};

RemainingQuantityProgressBar.propTypes = {
  initialQty: PropTypes.number.isRequired,
  remainingQty: PropTypes.number.isRequired,
  reconciledQtyPercentage: PropTypes.number.isRequired,
  baseUnits: PropTypes.string,
  actionWeight: PropTypes.number.isRequired,
  isActionWeightLoading: PropTypes.bool,
  showActionDetails: PropTypes.bool.isRequired,
};

export default RemainingQuantityProgressBar;
